/**
 * OpenStreetMap Overpass API Service
 * Fetches real infrastructure data for Jamaica
 */

const OVERPASS_API_URL = 'https://overpass-api.de/api/interpreter';

// Jamaica bounding box coordinates - Full island coverage
const JAMAICA_BBOX = {
  south: 17.68,    // Southern coast (Pedro Cays area)
  west: -78.37,    // Western tip (Negril)
  north: 18.53,    // Northern coast (near Port Antonio)
  east: -76.18     // Eastern tip (Morant Bay area)
};

/**
 * Build Overpass QL query for specific infrastructure types
 */
const buildOverpassQuery = (infraType, bbox = JAMAICA_BBOX) => {
  const { south, west, north, east } = bbox;
  
  const queries = {
    hospitals: `
      [out:json][timeout:25];
      (
        node["amenity"="hospital"](${south},${west},${north},${east});
        node["amenity"="clinic"](${south},${west},${north},${east});
        way["amenity"="hospital"](${south},${west},${north},${east});
        way["amenity"="clinic"](${south},${west},${north},${east});
      );
      out geom;
    `,
    
    schools: `
      [out:json][timeout:25];
      (
        node["amenity"="school"](${south},${west},${north},${east});
        node["amenity"="university"](${south},${west},${north},${east});
        way["amenity"="school"](${south},${west},${north},${east});
        way["amenity"="university"](${south},${west},${north},${east});
      );
      out geom;
    `,
    
    roads: `
      [out:json][timeout:25];
      (
        way["highway"~"^(primary|secondary|trunk|motorway)$"](${south},${west},${north},${east});
      );
      out geom;
    `,
    
    bridges: `
      [out:json][timeout:25];
      (
        way["bridge"="yes"](${south},${west},${north},${east});
        node["bridge"="yes"](${south},${west},${north},${east});
      );
      out geom;
    `,
    
    emergency: `
      [out:json][timeout:25];
      (
        node["amenity"~"^(fire_station|police)$"](${south},${west},${north},${east});
        way["amenity"~"^(fire_station|police)$"](${south},${west},${north},${east});
      );
      out geom;
    `,
    
    utilities: `
      [out:json][timeout:25];
      (
        node["power"="plant"](${south},${west},${north},${east});
        node["amenity"="fuel"](${south},${west},${north},${east});
        way["power"="plant"](${south},${west},${north},${east});
        way["amenity"="fuel"](${south},${west},${north},${east});
      );
      out geom;
    `,
    
    all: `
      [out:json][timeout:30];
      (
        node["amenity"~"^(hospital|clinic|school|university|fire_station|police|fuel)$"](${south},${west},${north},${east});
        way["amenity"~"^(hospital|clinic|school|university|fire_station|police|fuel)$"](${south},${west},${north},${east});
        way["highway"~"^(primary|secondary|trunk|motorway)$"](${south},${west},${north},${east});
        way["bridge"="yes"](${south},${west},${north},${east});
        node["power"="plant"](${south},${west},${north},${east});
      );
      out geom;
    `
  };
  
  return queries[infraType] || queries.all;
};

/**
 * Fetch infrastructure data from Overpass API
 */
export const fetchInfrastructureData = async (infraType = 'all', bbox = null) => {
  try {
    const query = buildOverpassQuery(infraType, bbox);
    
    const response = await fetch(OVERPASS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `data=${encodeURIComponent(query)}`
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    return processInfrastructureData(data.elements);
    
  } catch (error) {
    console.error('Error fetching infrastructure data:', error);
    throw error;
  }
};

/**
 * Process raw OSM data into usable format
 */
const processInfrastructureData = (elements) => {
  const processedData = [];
  
  elements.forEach(element => {
    try {
      let lat, lon, name, type, category;
      
      // Handle nodes (points)
      if (element.type === 'node' && element.lat && element.lon) {
        lat = element.lat;
        lon = element.lon;
      }
      // Handle ways (lines/polygons) - use center point
      else if (element.type === 'way' && element.geometry) {
        const coords = element.geometry;
        if (coords.length > 0) {
          // Calculate centroid
          const latSum = coords.reduce((sum, coord) => sum + coord.lat, 0);
          const lonSum = coords.reduce((sum, coord) => sum + coord.lon, 0);
          lat = latSum / coords.length;
          lon = lonSum / coords.length;
        }
      }
      
      if (!lat || !lon) return;
      
      // Extract tags
      const tags = element.tags || {};
      name = tags.name || tags['name:en'] || 'Unknown';
      
      // Categorize infrastructure
      if (tags.amenity === 'hospital' || tags.amenity === 'clinic') {
        category = 'healthcare';
        type = tags.amenity;
      } else if (tags.amenity === 'school' || tags.amenity === 'university') {
        category = 'education';
        type = tags.amenity;
      } else if (tags.amenity === 'fire_station' || tags.amenity === 'police') {
        category = 'emergency';
        type = tags.amenity;
      } else if (tags.amenity === 'fuel' || tags.power === 'plant') {
        category = 'utilities';
        type = tags.amenity || tags.power;
      } else if (tags.highway) {
        category = 'transportation';
        type = tags.highway;
        name = tags.name || `${tags.highway} road`;
      } else if (tags.bridge === 'yes') {
        category = 'transportation';
        type = 'bridge';
        name = tags.name || 'Bridge';
      } else {
        category = 'other';
        type = 'unknown';
      }
      
      processedData.push({
        id: element.id,
        lat,
        lon,
        name,
        type,
        category,
        tags,
        // Add simulated damage status for demo
        damageStatus: simulateDamageStatus(lat, lon, category),
        lastUpdated: new Date().toISOString()
      });
      
    } catch (error) {
      console.warn('Error processing element:', element, error);
    }
  });
  
  return processedData;
};

/**
 * Simulate damage status based on location and infrastructure type
 * In a real scenario, this would come from actual damage assessment data
 */
const simulateDamageStatus = (lat, lon, category) => {
  // St. Catherine East Central (hurricane path) - more severe damage
  const stCatherineCenter = [17.9857, -76.8857];
  const distanceToEpicenter = Math.sqrt(
    Math.pow(lat - stCatherineCenter[0], 2) + Math.pow(lon - stCatherineCenter[1], 2)
  );
  
  // Critical infrastructure is prioritized for assessment
  const criticalTypes = ['healthcare', 'emergency', 'utilities'];
  const isCritical = criticalTypes.includes(category);
  
  let severity;
  if (distanceToEpicenter < 0.1) {
    severity = Math.random() < 0.6 ? 'major' : 'destroyed';
  } else if (distanceToEpicenter < 0.3) {
    severity = Math.random() < 0.4 ? 'major' : 'moderate';
  } else if (distanceToEpicenter < 0.6) {
    severity = Math.random() < 0.3 ? 'moderate' : 'minor';
  } else {
    severity = Math.random() < 0.2 ? 'minor' : 'operational';
  }
  
  return {
    severity,
    assessed: isCritical || Math.random() < 0.7,
    assessmentDate: new Date(Date.now() - Math.random() * 86400000 * 3).toISOString(), // Last 3 days
    notes: generateDamageNotes(severity, category)
  };
};

/**
 * Generate realistic damage assessment notes
 */
const generateDamageNotes = (severity, category) => {
  const notes = {
    healthcare: {
      destroyed: 'Facility completely destroyed. Emergency services relocated.',
      major: 'Significant structural damage. Partially operational.',
      moderate: 'Roof damage, power outages. Limited services available.',
      minor: 'Minor damage to windows/exterior. Fully operational.',
      operational: 'No significant damage reported. Operational.'
    },
    education: {
      destroyed: 'School building collapsed. Classes suspended indefinitely.',
      major: 'Severe roof/wall damage. Building unsafe for occupation.',
      moderate: 'Flooding and wind damage. Repairs needed before reopening.',
      minor: 'Minor damage to grounds and exterior. Expected to reopen soon.',
      operational: 'Minimal damage. Used as emergency shelter.'
    },
    emergency: {
      destroyed: 'Station destroyed. Services relocated to temporary facility.',
      major: 'Significant damage but emergency services maintained.',
      moderate: 'Partial damage. Reduced capacity but operational.',
      minor: 'Minor damage. Full emergency services available.',
      operational: 'No damage. Actively responding to hurricane impacts.'
    },
    transportation: {
      destroyed: 'Road/bridge impassable. Complete reconstruction needed.',
      major: 'Major damage. Limited access for emergency vehicles only.',
      moderate: 'Debris and damage. Passable with caution.',
      minor: 'Minor debris. Cleared and operational.',
      operational: 'Clear and fully operational.'
    },
    utilities: {
      destroyed: 'Facility destroyed. Service disrupted indefinitely.',
      major: 'Major damage. Partial service restoration in progress.',
      moderate: 'Equipment damage. Service intermittent.',
      minor: 'Minor damage. Service mostly restored.',
      operational: 'Operational. Providing emergency services.'
    }
  };
  
  return notes[category]?.[severity] || `${severity} damage reported.`;
};

/**
 * Get infrastructure data for a specific parish
 */
export const fetchParishInfrastructure = async (parishName) => {
  // Parish bounding boxes (approximate)
  const parishBounds = {
    'St. Catherine': { south: 17.85, west: -77.25, north: 18.15, east: -76.75 },
    'Kingston': { south: 17.92, west: -76.85, north: 18.02, east: -76.75 },
    'St. Andrew': { south: 17.98, west: -76.85, north: 18.12, east: -76.65 },
    'Clarendon': { south: 17.75, west: -77.65, north: 18.05, east: -77.05 },
    'Manchester': { south: 17.90, west: -77.68, north: 18.18, east: -77.35 }
  };
  
  const bbox = parishBounds[parishName] || JAMAICA_BBOX;
  return fetchInfrastructureData('all', bbox);
};

const osmService = {
  fetchInfrastructureData,
  fetchParishInfrastructure,
  JAMAICA_BBOX
};

export default osmService;
